import { useEffect, useState } from "react"
import { StarRatingIcon, CameraIcon, ChatIcon } from "../../lib/cccIcons"
import { supabase } from "../../lib/supabase"
import { useAuth } from "../../contexts/AuthContext"
import { useLanguage } from "../../contexts/LanguageContext"
import toast from "react-hot-toast"
import { filterReviewContent } from "../../lib/reviewFilter"
import { useLocation } from "react-router-dom"

function StarRating({ value, onChange, label }) {
  return (
    <div style={{ marginBottom:"1rem" }}>
      <div style={{ fontSize:11, color:"#666", textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:8 }}>{label}</div>
      <div style={{ display:"flex", gap:6 }}>
        {[1,2,3,4,5].map(star=>(
          <button key={star} type="button" onClick={()=>onChange(star)}
            style={{ background:"none", border:"none", cursor:"pointer", fontSize:28, color:star<=value?"#e6821e":"#555555", padding:"2px" }}>★</button>
        ))}
      </div>
      {value>0&&<div style={{ fontSize:11, color:"#e6821e", marginTop:4 }}>{["","Poor","Fair","Good","Very good","Excellent"][value]}</div>}
    </div>
  )
}

export default function CustomerReviews() {
  const { user } = useAuth()
  const { t } = useLanguage()
  const [completedBookings, setCompletedBookings] = useState([])
  const [myReviews, setMyReviews] = useState([])
  const [receivedReviews, setReceivedReviews] = useState([])
  const [respondingTo, setRespondingTo] = useState(null)
  const [responseText, setResponseText] = useState("")
  const [submittingResponse, setSubmittingResponse] = useState(false)
  const [tab, setTab] = useState("pending")
  const [loading, setLoading] = useState(true)
  const [reviewing, setReviewing] = useState(null)
  const [form, setForm] = useState({ provider_rating:0, provider_review:"", driver_rating:0, driver_review:"", mechanic_rating:0, mechanic_review:"" })
  const [submitting, setSubmitting] = useState(false)
  const [photos, setPhotos] = useState([])
  const [uploading, setUploading] = useState(false)

  const location = useLocation()
  const preselectedBooking = new URLSearchParams(location.search).get("booking")
  useEffect(() => { if (user) load() }, [user])
  useEffect(() => {
    if (preselectedBooking && completedBookings.length > 0) {
      const b = completedBookings.find(b=>b.id===preselectedBooking)
      if(b) { setReviewing(b); setTab("pending") }
    }
  }, [preselectedBooking, completedBookings])

  async function load() {
    const [{ data: bookings }, { data: reviews }, { data: received }] = await Promise.all([
      supabase.from("bookings").select("*").eq("customer_id",user.id).eq("status","completed").order("created_at",{ascending:false}),
      supabase.from("reviews").select("*, profile_public!reviews_provider_id_fkey(first_name,last_name,business_name)").eq("customer_id",user.id).order("created_at",{ascending:false}),
      supabase.from("reviews").select("*, provider:profile_public!reviews_provider_id_fkey(first_name,last_name,business_name)").eq("customer_id",user.id).not("customer_rating","is",null).order("created_at",{ascending:false})
    ])
    setCompletedBookings(bookings||[])
    setMyReviews(reviews||[])
    setReceivedReviews(received||[])
    setLoading(false)
  }

  const reviewedBookingIds = new Set(myReviews.map(r=>r.booking_id))
  const pendingReview = completedBookings.filter(b=>!reviewedBookingIds.has(b.id))

  async function submitResponse(reviewId) {
    if(!responseText.trim()) return toast.error("Please write a response")
    const check = filterReviewContent(responseText)
    if(check.hasContactInfo || check.hasBadWords) return toast.error("Please keep your response professional")
    setSubmittingResponse(true)
    await supabase.from("reviews").update({ customer_response: responseText }).eq("id", reviewId).eq("customer_id", user.id)
    toast.success("Response submitted!")
    setRespondingTo(null); setResponseText(""); setSubmittingResponse(false); load()
  }
  async function uploadPhoto(file) {
    const ext = file.name.split(".").pop()
    const path = "reviews/"+user.id+"/"+Date.now()+"."+ext
    const { error } = await supabase.storage.from("review-photos").upload(path, file)
    if (error) throw error
    const { data } = supabase.storage.from("review-photos").getPublicUrl(path)
    return data.publicUrl
  }
  async function handlePhotoSelect(e) {
    const files = Array.from(e.target.files).slice(0,3)
    setUploading(true)
    try {
      const urls = await Promise.all(files.map(uploadPhoto))
      setPhotos(prev=>[...prev,...urls].slice(0,3))
    } catch(err) { toast.error("Photo upload failed: "+err.message) }
    setUploading(false)
  }
  async function submitReview(e) {
    e.preventDefault()
    if (form.provider_rating===0) return toast.error(t("error"))
    // Filter review content
    const reviewCheck = filterReviewContent(form.provider_review)
    if (reviewCheck.hasContactInfo) return toast.error("Reviews cannot contain contact details (phone/email). Please keep all communication on the platform.")
    if (reviewCheck.hasBadWords) return toast.error("Your review contains inappropriate language. Please keep it professional.")
    const driverCheck = filterReviewContent(form.driver_review)
    if (driverCheck.hasContactInfo || driverCheck.hasBadWords) return toast.error("Driver review contains inappropriate content. Please keep it professional.")
    setSubmitting(true)
    try {
      const { error } = await supabase.from("reviews").insert({
        booking_id:reviewing.id, customer_id:user.id, provider_id:reviewing.provider_id,
        driver_id:reviewing.driver_id||null, provider_rating:form.provider_rating,
        mechanic_id:reviewing.assigned_mechanic_id||null,
        mechanic_rating:reviewing.assigned_mechanic_id?form.mechanic_rating||null:null,
        mechanic_review:reviewing.assigned_mechanic_id&&form.mechanic_review?form.mechanic_review:null,
        provider_review:form.provider_review||null,
        driver_rating:reviewing.is_concierge&&form.driver_rating>0?form.driver_rating:null,
        driver_review:reviewing.is_concierge&&form.driver_review?form.driver_review:null,
        photo_urls: photos.length>0 ? photos : null,
      })
      if (error) throw error

      // Feed this rating into probation tracking (no-op while the probation system
      // toggle is off, or if this driver isn\'t currently on probation)
      if (reviewing.is_concierge && reviewing.driver_id && form.driver_rating>0) {
        supabase.rpc("record_driver_probation_job", { p_driver_id: reviewing.driver_id, p_rating: form.driver_rating }).then(()=>{})
      }

      // Send push notification to provider
      try {
        await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-push`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}` },
          body: JSON.stringify({
            user_id: reviewing.provider_id,
            title: "New Review Received! ⭐",
            message: `${form.provider_rating} star review: ${form.provider_review||"No comment"}`.substring(0,100)
          })
        })
        // Also notify driver if rated
        if(reviewing.driver_id && form.driver_rating>0) {
          await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-push`, {
            method: "POST",
            headers: { "Content-Type": "application/json", "Authorization": `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}` },
            body: JSON.stringify({
              user_id: reviewing.driver_id,
              title: "New Driver Review! ⭐",
              message: `${form.driver_rating} star rating: ${form.driver_review||"No comment"}`.substring(0,100)
            })
          })
        }
      } catch(pushErr) { console.log("Push notification failed:", pushErr.message) }
      // Award 50 bonus points for leaving a review (loyalty_points is the real table CustomerLoyalty.jsx reads from)
      const { data: lp } = await supabase.from("loyalty_points").select("points,lifetime_points").eq("user_id", user.id).maybeSingle()
      await supabase.from("loyalty_points").upsert({
        user_id: user.id,
        points: (lp?.points||0) + 50,
        lifetime_points: (lp?.lifetime_points||0) + 50,
      }, { onConflict: "user_id" })
      await supabase.from("notifications").insert({
        user_id: user.id,
        title: "Review Bonus!",
        message: "Thanks for your review! You earned 50 bonus points.",
        type: "success",
      })

      // Save mechanic rating to booking and update mechanic's average rating
      if (reviewing.assigned_mechanic_id && form.mechanic_rating > 0) {
        await supabase.from("bookings").update({
          mechanic_rating: form.mechanic_rating,
          mechanic_rating_comment: form.mechanic_review
        }).eq("id", reviewing.id)

        const { data: mechRatings } = await supabase.from("bookings")
          .select("mechanic_rating")
          .eq("assigned_mechanic_id", reviewing.assigned_mechanic_id)
          .not("mechanic_rating", "is", null)
        if (mechRatings?.length) {
          const avg = mechRatings.reduce((s,r)=>s+r.mechanic_rating,0)/mechRatings.length
          await supabase.from("mechanics").update({
            rating: Math.round(avg*10)/10,
            total_reviews: mechRatings.length
          }).eq("id", reviewing.assigned_mechanic_id)
        }
      }

      toast.success(t("success") + " +50 points!")
      setReviewing(null)
      setPhotos([])
      setForm({ provider_rating:0, provider_review:"", driver_rating:0, driver_review:"", mechanic_rating:0, mechanic_review:"" })
      load()
    } catch(err) {
      toast.error(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  const pendingLabel = t("language")==="sw" ? "Zinazosubiri" : "Pending reviews"
  const submittedLabel = t("language")==="sw" ? "Maoni yangu" : "My reviews"
  const inp = { width:"100%", background:"#ffffff", border:"1px solid #e5e5e5", borderRadius:8, padding:"10px 12px", color:"#000000", fontSize:13, outline:"none", fontFamily:"'DM Sans',sans-serif", resize:"vertical" }

  return (
    <div>
      <div style={{ display:"flex", gap:6, marginBottom:"1.25rem" }}>
        {[
          { k:"pending", l:`${pendingLabel} (${pendingReview.length})` },
          { k:"submitted", l:`${submittedLabel} (${myReviews.length})` },
          { k:"received", l:`Reviews about me (${receivedReviews.length})` },
        ].map(t2=>(
          <button key={t2.k} onClick={()=>setTab(t2.k)}
            style={{ padding:"8px 16px", borderRadius:8, border:"none", fontSize:12, cursor:"pointer", background:tab===t2.k?"#e6821e":"#555555", color:tab===t2.k?"#fff":"#ccc", fontFamily:"'DM Sans',sans-serif", fontWeight:tab===t2.k?700:400 }}>
            {t2.l}
          </button>
        ))}
      </div>

      {loading&&<div style={{ color:"#777777", fontSize:13 }}>{t("loading")}</div>}

      {tab==="pending"&&!reviewing&&(
        <div>
          {pendingReview.length===0&&!loading&&<div style={{ color:"#888888", fontSize:13, textAlign:"center", padding:"2rem" }}>{t("noDataYet")}</div>}
          {pendingReview.map(b=>(
            <div key={b.id} style={{ background:"#ffffff", border:"1px solid #eeeeee", borderRadius:10, padding:"1rem", marginBottom:8, display:"flex", alignItems:"center", gap:12 }}>
              <div style={{ width:42, height:42, background:"#fff8f0", borderRadius:9, display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, flexShrink:0 }}>🔧</div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:14, fontWeight:500, color:"#000000" }}>{b.service_name}</div>
                <div style={{ fontSize:11, color:"#777777", marginTop:2 }}>{b.booking_date} · KES {Number(b.total_amount).toLocaleString()}</div>
              </div>
              <button onClick={()=>{ setReviewing(b); setForm({ provider_rating:0, provider_review:"", driver_rating:0, driver_review:"" }) }}
                style={{ background:"#e6821e", border:"none", borderRadius:8, color:"#fff", fontSize:12, fontWeight:700, padding:"8px 14px", cursor:"pointer", fontFamily:"Syne,sans-serif", flexShrink:0 }}>
                {t("language")==="sw"?"Kadiria":"Rate"}
              </button>
            </div>
          ))}
        </div>
      )}

      {tab==="pending"&&reviewing&&(
        <div>
          <button onClick={()=>setReviewing(null)} style={{ background:"none", border:"none", color:"#666", cursor:"pointer", fontSize:13, marginBottom:"1rem", fontFamily:"'DM Sans',sans-serif", padding:0 }}>
            ← {t("back")}
          </button>
          <div style={{ background:"#ffffff", border:"1px solid #eeeeee", borderRadius:12, padding:"1.25rem" }}>
            <div style={{ fontFamily:"Syne", fontSize:15, fontWeight:800, color:"#000000", marginBottom:2 }}>{reviewing.service_name}</div>
            <div style={{ fontSize:11, color:"#777777", marginBottom:"1.5rem" }}>{reviewing.booking_date}</div>
            <form onSubmit={submitReview}>
              <StarRating value={form.provider_rating} onChange={v=>setForm(f=>({...f,provider_rating:v}))} label={t("language")==="sw"?"Kadiria mtoa huduma":"Rate the service provider"}/>
              <div style={{ marginBottom:"1rem" }}>
                <div style={{ fontSize:11, color:"#666", textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:6 }}>{t("language")==="sw"?"Maoni (hiari)":"Review (optional)"}</div>
                <textarea value={form.provider_review} onChange={e=>setForm(f=>({...f,provider_review:e.target.value}))} rows={3} placeholder={t("language")==="sw"?"Shiriki uzoefu wako...":"Share your experience..."} style={inp}/>
              </div>
              {reviewing.is_concierge&&(
                <>
                  <div style={{ height:1, background:"#f0f0f0", margin:"1rem 0" }}/>
                  <StarRating value={form.driver_rating} onChange={v=>setForm(f=>({...f,driver_rating:v}))} label={t("language")==="sw"?"Kadiria dereva (hiari)":"Rate the driver (optional)"}/>
                </>
              )}
              {/* Mechanic rating - shown if mechanic was assigned */}
              {reviewing.assigned_mechanic_id&&(
                <>
                  <hr style={{ border:"none", borderTop:"1px solid #f0f0f0", margin:"1rem 0" }}/>
                  <StarRating value={form.mechanic_rating} onChange={v=>setForm(f=>({...f,mechanic_rating:v}))} label="Rate the mechanic (optional)"/>
                  {form.mechanic_rating>0&&(
                    <textarea value={form.mechanic_review} onChange={e=>setForm(f=>({...f,mechanic_review:e.target.value}))}
                      placeholder="Comment about the mechanic's work..."
                      rows={2} style={{ width:"100%", background:"#f8f8f8", border:"1px solid #eeeeee", borderRadius:8, padding:"10px 12px", fontSize:12, color:"#000", outline:"none", resize:"none", marginTop:8, boxSizing:"border-box" }}/>
                  )}
                </>
              )}
              {/* Photo upload */}
              <div style={{ marginBottom:"1rem" }}>
                <div style={{ fontSize:11, color:"#666", textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:6 }}>Add photos (optional)</div>
                <input type="file" accept="image/*" multiple onChange={handlePhotoSelect} style={{ display:"none" }} id="review-photos"/>
                <label htmlFor="review-photos" style={{ display:"inline-block", background:"#f8f8f8", border:"1px solid #eee", borderRadius:8, padding:"8px 14px", fontSize:12, cursor:"pointer", color:"#555" }}>
                  {uploading?"Uploading...":<><CameraIcon size={14} color="currentColor"/> Add photos</>}
                </label>
                {photos.length>0&&(
                  <div style={{ display:"flex", gap:6, marginTop:8, flexWrap:"wrap" }}>
                    {photos.map((url,i)=>(
                      <div key={i} style={{ position:"relative" }}>
                        <img src={url} alt="" style={{ width:60, height:60, objectFit:"cover", borderRadius:8, border:"1px solid #eee" }}/>
                        <button type="button" onClick={()=>setPhotos(p=>p.filter((_,j)=>j!==i))} style={{ position:"absolute", top:-4, right:-4, background:"#e24b4a", border:"none", borderRadius:"50%", width:16, height:16, color:"#fff", fontSize:10, cursor:"pointer" }}>×</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <button type="submit" disabled={submitting||form.provider_rating===0||uploading}
                style={{ width:"100%", background:form.provider_rating>0?"#e6821e":"#555555", border:"none", borderRadius:9, color:form.provider_rating>0?"#fff":"#ccc", fontFamily:"Syne,sans-serif", fontSize:14, fontWeight:700, padding:"13px", cursor:form.provider_rating>0?"pointer":"not-allowed" }}>
                {submitting?t("loading"):t("submit")}
              </button>
            </form>
          </div>
        </div>
      )}

      {tab==="received"&&(
        <div>
          {receivedReviews.length===0&&<div style={{ color:"#888", fontSize:13, textAlign:"center", padding:"2rem" }}>No reviews about you yet</div>}
          {receivedReviews.map(r=>(
            <div key={r.id} style={{ background:"#fff", border:"1px solid #eee", borderRadius:10, padding:"1rem", marginBottom:8 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:8 }}>
                <div>
                  <div style={{ fontSize:13, fontWeight:600 }}>{r.provider?.business_name||r.provider?.first_name} {r.provider?.last_name}</div>
                  <div style={{ fontSize:10, color:"#888", marginTop:2 }}>{new Date(r.created_at).toLocaleDateString()}</div>
                </div>
                <div style={{ display:"flex", gap:1 }}>{[1,2,3,4,5].map(s=><span key={s} style={{ color:s<=r.customer_rating?"#378add":"#ddd", fontSize:16 }}>★</span>)}</div>
              </div>
              {r.customer_review&&<div style={{ fontSize:12, color:"#555", fontStyle:"italic", marginBottom:8 }}>"{"}{"}r.customer_review{"}"}"</div>}
              {r.customer_response?(
                <div style={{ background:"#f0fdf4", border:"1px solid #1d9e7520", borderRadius:8, padding:"0.5rem 0.75rem" }}>
                  <div style={{ fontSize:10, color:"#1d9e75", fontWeight:600, marginBottom:2 }}>Your response</div>
                  <div style={{ fontSize:12, color:"#333" }}>{r.customer_response}</div>
                </div>
              ):respondingTo===r.id?(
                <div>
                  <textarea value={responseText} onChange={e=>setResponseText(e.target.value)} rows={3} placeholder="Write a professional response..."
                    style={{ width:"100%", background:"#f8f8f8", border:"1px solid #ddd", borderRadius:8, padding:"10px 12px", fontSize:12, outline:"none", resize:"none", boxSizing:"border-box" }}/>
                  <div style={{ display:"flex", gap:8, marginTop:6 }}>
                    <button onClick={()=>submitResponse(r.id)} disabled={submittingResponse} style={{ background:"#378add", border:"none", borderRadius:7, color:"#fff", fontSize:12, fontWeight:700, padding:"8px 16px", cursor:"pointer" }}>{submittingResponse?"Posting...":"Post Response"}</button>
                    <button onClick={()=>{ setRespondingTo(null); setResponseText("") }} style={{ background:"#f0f0f0", border:"none", borderRadius:7, color:"#555", fontSize:12, padding:"8px 14px", cursor:"pointer" }}>Cancel</button>
                  </div>
                </div>
              ):(
                <button onClick={()=>{ setRespondingTo(r.id); setResponseText("") }} style={{ background:"#eff6ff", border:"1px solid #378add30", borderRadius:7, color:"#378add", fontSize:11, padding:"5px 12px", cursor:"pointer" }}>💬 Respond</button>
              )}
            </div>
          ))}
        </div>
      )}
      {tab==="submitted"&&(
        <div>
          {myReviews.length===0&&<div style={{ color:"#888888", fontSize:13, textAlign:"center", padding:"2rem" }}>{t("noDataYet")}</div>}
          {myReviews.map(r=>(
            <div key={r.id} style={{ background:"#ffffff", border:"1px solid #eeeeee", borderRadius:10, padding:"1rem", marginBottom:8 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:8 }}>
                <div>
                  <div style={{ fontSize:13, fontWeight:500, color:"#000000" }}>
                    {r.profile_public?.business_name||`${r.profile_public?.first_name||""} ${r.profile_public?.last_name||""}`}
                  </div>
                  <div style={{ fontSize:10, color:"#888888", marginTop:2 }}>{new Date(r.created_at).toLocaleDateString()}</div>
                </div>
                <div style={{ display:"flex", gap:1 }}>
                  {[1,2,3,4,5].map(s=><span key={s} style={{ color:s<=r.provider_rating?"#e6821e":"#555555", fontSize:16 }}>★</span>)}
                </div>
              </div>
              {r.provider_review&&<div style={{ fontSize:12, color:"#555555", lineHeight:1.5, marginBottom:8 }}>&quot;{r.provider_review}&quot;</div>}
              {r.photo_urls?.length>0&&(
                <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:8 }}>
                  {r.photo_urls.map((url,i)=>(
                    <img key={i} src={url} alt="" onClick={()=>window.open(url,"_blank")}
                      style={{ width:70, height:70, objectFit:"cover", borderRadius:8, cursor:"pointer", border:"1px solid #eee" }}/>
                  ))}
                </div>
              )}
              {r.provider_response&&(
                <div style={{ background:"#ffffff", border:"1px solid #e5e5e5", borderRadius:8, padding:"0.75rem", marginTop:8 }}>
                  <div style={{ fontSize:10, color:"#777777", marginBottom:4 }}>{t("language")==="sw"?"Jibu la mtoa huduma":"Provider reply"}</div>
                  <div style={{ fontSize:12, color:"#666666", lineHeight:1.5 }}>{r.provider_response}</div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}




